import {useCallback, useContext, useEffect} from 'react';
import {fabric} from 'fabric';
import {EventCreateStickerContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Sticker/EventCreateStickerContext';
import {fabricGif} from '~/common/canvas/animation/gif/Gif';
import {ELEMENT_WIDTH} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Sticker/components/Stickers/constants';

function getFileExtensionFromUrl(url: string): string {
  if (!url.startsWith('data:')) return url.split('.').pop() || '';
  const semi = url.indexOf(';', 5);
  if (semi === -1) return '';
  const mime = url.substring(5, semi);
  const slash = mime.indexOf('/');
  return slash === -1 ? '' : mime.substring(slash + 1);
}

interface ExtendedImageOptions extends fabric.IImageOptions {
  originalUrl?: string;
  id?: string;
  src?: string;
  type?: string;
}

export interface UseStickerCanvasOptions {
  stickerMediasMapping: Record<string, string>;
  customStickerMediasMapping: Record<string, string>;
  isCustom: boolean;
  setActiveId: (id: string) => void;
  activeId: string;
  isDesktop: boolean;
}

export function useStickerCanvasAdder({
  stickerMediasMapping,
  customStickerMediasMapping,
  isCustom,
  setActiveId,
  activeId,
  isDesktop,
}: UseStickerCanvasOptions) {
  const {canvas} = useContext(EventCreateStickerContext);

  useEffect(
    () => () => {
      if (canvas?.getActiveObject()) {
        canvas?.discardActiveObject()?.requestRenderAll();
      }
    },
    [canvas]
  );

  // prepare & add element whenever activeId changes
  const prepElementToAdd = useCallback(async () => {
    if (!activeId || !canvas) return;

    const map = isCustom ? customStickerMediasMapping : stickerMediasMapping;
    const url = map[activeId];
    const ext = getFileExtensionFromUrl(url);
    const isGif = ext === 'gif';

    const addObj = (obj: fabric.Image) => {
      obj.set({
        top: isDesktop ? obj.top! + 50 : obj.top! + 100,
        left: obj.left! + 50,
      });
      canvas.add(obj);
      if (isGif) {
        (function renderLoop() {
          canvas.renderAll();
          fabric.util.requestAnimFrame(renderLoop);
        })();
      }
      setActiveId(''); // clear selection
      canvas.setActiveObject(obj);
    };

    if (isGif) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error typescript doesn't like that the third argument can be null in the js function
      const gifObj = await fabricGif(url, {originalUrl: url, id: activeId}, ELEMENT_WIDTH);
      addObj(gifObj as fabric.Image);
    } else {
      fabric.Image.fromURL(url, (img) => {
        img.scaleToWidth(ELEMENT_WIDTH);
        img.set({
          originalUrl: url,
          src: url,
          type: 'UYOImage',
          id: activeId,
        } as ExtendedImageOptions);
        addObj(img);
      });
    }
  }, [activeId, canvas, customStickerMediasMapping, isCustom, setActiveId, stickerMediasMapping]);

  useEffect(() => {
    prepElementToAdd();
  }, [activeId, prepElementToAdd]);

  return {};
}
